import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSubscriptionStore, useUserStore } from "store";
import { getCsrfToken } from "../../utils";

import { can } from '../../utils/access';
// "Yleiset" sub-tab of the Ominaisuudet (Features) settings tab.
// Holds the simple on/off feature toggles that need no additional configuration.
const SIMPLE_FLAGS = [
  "service_bays_enabled",
  "work_hours_reporting_enabled",
  "inventory_tracking_enabled",
  "parts_order_tracking_enabled",
  "vendor_catalogues_enabled",
];

function FeaturesGeneralTab({ t, onDirtyChange }) {
  const { currentOrganization, fetchUser } = useUserStore();
  const [form, setForm] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [oemConfirmOpen, setOemConfirmOpen] = useState(false);
  // The customer approval email setting is not in the user store (the recipient address is
  // not needed by every member), so it is loaded from the organization itself. null = not loaded.
  const [notify, setNotify] = useState(null);
  const [notifyEmailError, setNotifyEmailError] = useState("");
  const [notifyLoadError, setNotifyLoadError] = useState(false);
  // Cancels the load in flight, so a late answer for another organization (after an
  // organization switch, or from a retry) never lands in this form.
  const cancelNotifyLoadRef = useRef(() => {});

  const isAdmin = can(currentOrganization, "settings.features");
  // OEM is refused for trials by the backend (oem_service/permissions.py); disable
  // the toggle rather than letting the save come back 403. Subscription info is not
  // fetched app-wide, so this tab loads it the same way TimeclockSettingsTab does.
  const fetchSubscriptionInfo = useSubscriptionStore((s) => s.fetchSubscriptionInfo);
  const canUseOemService = useSubscriptionStore((s) => s.canUseOemService)();

  useEffect(() => {
    fetchSubscriptionInfo();
  }, [fetchSubscriptionInfo]);

  // Seed local state from the store. work_hours_reporting_enabled defaults to true.
  useEffect(() => {
    if (!currentOrganization) return;
    setForm({
      service_bays_enabled: currentOrganization.service_bays_enabled || false,
      work_hours_reporting_enabled: currentOrganization.work_hours_reporting_enabled ?? true,
      inventory_tracking_enabled: currentOrganization.inventory_tracking_enabled || false,
      parts_order_tracking_enabled: currentOrganization.parts_order_tracking_enabled || false,
      vendor_catalogues_enabled: currentOrganization.vendor_catalogues_enabled || false,
      oem_service_enabled: currentOrganization.oem_service_enabled || false,
    });
    setIsDirty(false);
  }, [currentOrganization?.organization_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadNotify = useCallback(() => {
    cancelNotifyLoadRef.current();
    const organizationId = currentOrganization?.organization_id;
    if (!organizationId) return;
    let cancelled = false;
    cancelNotifyLoadRef.current = () => {
      cancelled = true;
    };
    setNotify(null);
    setNotifyLoadError(false);
    axios
      .get(`/api/organizations/${organizationId}/`, {
        headers: { "X-Organization-Id": organizationId },
      })
      .then((response) => {
        if (cancelled) return;
        setNotify({
          enabled: response.data.notify_on_customer_confirmation || false,
          email: response.data.customer_confirmation_notification_email || "",
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load notification settings:", error);
        setNotifyLoadError(true);
      });
  }, [currentOrganization?.organization_id]);

  useEffect(() => {
    loadNotify();
    return () => cancelNotifyLoadRef.current();
  }, [loadNotify]);

  const handleNotifyChange = (field) => (event) => {
    const value = field === "enabled" ? event.target.checked : event.target.value;
    setNotify((prev) => ({ ...prev, [field]: value }));
    setNotifyEmailError("");
    setIsDirty(true);
  };

  const handleToggle = (field) => (event) => {
    const checked = event.target.checked;
    setForm((prev) => ({ ...prev, [field]: checked }));
    setIsDirty(true);
  };

  // Enabling OEM starts per-lookup billing — require an explicit price consent.
  const handleOemToggle = (event) => {
    const checked = event.target.checked;
    if (checked) {
      setOemConfirmOpen(true);
      return;
    }
    setForm((prev) => ({ ...prev, oem_service_enabled: false }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!form) return;
    if (notify?.enabled && !notify.email.trim()) {
      setNotifyEmailError(t("confirmation.notifyEmailRequired"));
      return;
    }
    try {
      setSaving(true);
      const payload = {};
      SIMPLE_FLAGS.forEach((flag) => {
        payload[flag] = form[flag];
      });
      // oem_service_enabled is admin-only on the backend: send it only when it
      // actually changed, so a foreman saving other toggles never trips the 403.
      if (form.oem_service_enabled !== (currentOrganization.oem_service_enabled || false)) {
        payload.oem_service_enabled = form.oem_service_enabled;
      }
      if (notify) {
        payload.notify_on_customer_confirmation = notify.enabled;
        payload.customer_confirmation_notification_email = notify.email.trim();
      }
      // Osamyynti settings live on their own sub-tab (FeaturesPartsSalesTab).
      await axios.patch(
        `/api/organizations/${currentOrganization.organization_id}/`,
        payload,
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );
      // Refresh user store so nav menu and feature gates update immediately
      await fetchUser();
      setIsDirty(false);
      setSnackbar({
        open: true,
        message: t("Organization updated successfully"),
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update features:", error);
      const emailError = error.response?.data?.customer_confirmation_notification_email;
      if (emailError) {
        setNotifyEmailError(Array.isArray(emailError) ? emailError[0] : String(emailError));
      }
      const message = error.response?.data?.detail || t("Failed to update organization");
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Report dirty state to parent (sub-tab + tab-switch guards).
  // Use a ref so the reported save closure always calls the latest handleSave.
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    onDirtyChange?.(isDirty, () => handleSaveRef.current());
  }, [isDirty, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.service_bays_enabled}
                onChange={handleToggle("service_bays_enabled")}
              />
            }
            label={t("Enable Service Bays")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("Allow scheduling by service bay in addition to employees")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.work_hours_reporting_enabled}
                onChange={handleToggle("work_hours_reporting_enabled")}
              />
            }
            label={t("Enable Work Hours Reporting")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("Enable work hours tracking and reporting for employees")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.inventory_tracking_enabled}
                onChange={handleToggle("inventory_tracking_enabled")}
              />
            }
            label={t("inventory.trackingEnabled")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("inventory.trackingEnabledHelp")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.parts_order_tracking_enabled}
                onChange={handleToggle("parts_order_tracking_enabled")}
              />
            }
            label={t("parts_order.trackingEnabled")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("parts_order.trackingEnabledHelp")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.vendor_catalogues_enabled}
                onChange={handleToggle("vendor_catalogues_enabled")}
                data-testid="vendor-catalogues-toggle"
              />
            }
            label={t("catalogues.featureToggle")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("catalogues.featureToggleHelp")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.oem_service_enabled}
                onChange={handleOemToggle}
                disabled={!isAdmin || (!canUseOemService && !form.oem_service_enabled)}
                data-testid="oem-service-toggle"
              />
            }
            label={t("oem.featureToggle")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("oem.featureToggleHelp")}
            {!isAdmin ? ` ${t("oem.featureToggleAdminOnly")}` : ""}
            {isAdmin && !canUseOemService && !form.oem_service_enabled
              ? ` ${t("oem.featureToggleTrialBlocked")}`
              : ""}
          </Typography>
        </Grid>

        {notifyLoadError && (
          <Grid item xs={12}>
            <Alert
              severity="warning"
              data-testid="confirmation-notify-load-error"
              action={
                <Button color="inherit" size="small" onClick={loadNotify}>
                  {t("confirmation.notifyLoadRetry")}
                </Button>
              }
            >
              {t("confirmation.notifyLoadError")}
            </Alert>
          </Grid>
        )}

        {notify && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={notify.enabled}
                  onChange={handleNotifyChange("enabled")}
                  data-testid="confirmation-notify-toggle"
                />
              }
              label={t("confirmation.notifySetting")}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
              {t("confirmation.notifySettingHelp")}
            </Typography>
            {notify.enabled && (
              <Box sx={{ ml: { xs: 0, sm: 6 }, mt: 1.5, maxWidth: 420 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  label={t("confirmation.notifyEmail")}
                  value={notify.email}
                  onChange={handleNotifyChange("email")}
                  error={!!notifyEmailError}
                  helperText={notifyEmailError || " "}
                  inputProps={{ "data-testid": "confirmation-notify-email" }}
                />
              </Box>
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !isDirty}>
              {saving ? <CircularProgress size={20} /> : <span>{t("Save Changes")}</span>}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Price consent before enabling OEM huolto-ohjelma */}
      <Dialog open={oemConfirmOpen} onClose={() => setOemConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("oem.enableConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t("oem.enableConfirmBody")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOemConfirmOpen(false)}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            data-testid="oem-enable-confirm"
            onClick={() => {
              setForm((prev) => ({ ...prev, oem_service_enabled: true }));
              setIsDirty(true);
              setOemConfirmOpen(false);
            }}
          >
            {t("oem.enableConfirmAccept")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(FeaturesGeneralTab);
